import React from 'react';
import { EscortRate } from '@/types';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Home, MapPin } from 'lucide-react';

// Helper function to format duration (Copied from RatesSection)
const formatDuration = (duration: string | number | undefined, t: (key: string, options?: { count: number }) => string): string => {
  if (duration === undefined || duration === null || duration === '') return '';
  
  // Check if it's purely numeric (or a string representing a number)
  const durationNum = typeof duration === 'string' ? parseInt(duration, 10) : duration;
  const isNumeric = !isNaN(durationNum) && duration.toString() === durationNum.toString();

  if (isNumeric && durationNum > 0) {
    // Assuming numeric duration is in minutes
    const hours = durationNum / 60;
    
    if (hours === 1) {
      return `1 ${t('common:hour', { count: 1 })}`;
    }
    if (hours > 1 && Number.isInteger(hours)) {
      return `${hours} ${t('common:hours', { count: hours })}`;
    }
    // Fallback to minutes if numeric but not exactly 1 or more full hours
    return `${durationNum} ${t('common:minutes', { count: durationNum })}`;
  } else if (typeof duration === 'string') {
    // If it's a non-empty string but not purely numeric, return it as is
    return duration; 
  }
  
  return ''; // Return empty for other cases (like non-string, non-numeric)
};

interface RatesDisplayProps {
  rates: EscortRate[] | undefined;
  selectedRateId: string | null;
  onRateSelect: (rateId: string) => void;
  selectedCategory: string | null;
  onCategorySelect: (category: string) => void;
  availableCategories: string[];
  t: (key: string, options?: any) => string;
}

export const RatesDisplay: React.FC<RatesDisplayProps> = ({ 
  rates,
  selectedRateId, 
  onRateSelect, 
  selectedCategory, 
  onCategorySelect,
  availableCategories,
  t 
}) => {
  if (!rates || rates.length === 0) {
    return (
      <div className="mt-6 p-3 bg-muted/50 rounded-md text-center text-muted-foreground">
        {t('profile:no_rates_available')}
      </div>
    );
  }

  // Filter rates based on the selected category
  const filteredRates = rates.filter(rate => rate.category === selectedCategory);

  return (
    <div className="mt-4 space-y-4">
      {/* Category Selection Buttons */}
      <div>
        <h3 className="text-sm font-medium mb-2">{t('profile:select_category', { defaultValue: 'Type de rencontre' })}</h3>
        <div className="flex space-x-2">
          {availableCategories.includes('incall') && (
            <Button
              variant={selectedCategory === 'incall' ? 'default' : 'outline'}
              onClick={() => onCategorySelect('incall')}
              className={`flex-1 ${selectedCategory === 'incall' ? 'bg-pink-500 hover:bg-pink-600 text-white' : 'hover:bg-pink-50'}`}
            >
              <Home size={16} className="mr-2" />
              {t('profile:rates_incall', { defaultValue: 'Incall' })}
            </Button>
          )}
          {availableCategories.includes('outcall') && (
            <Button
              variant={selectedCategory === 'outcall' ? 'default' : 'outline'}
              onClick={() => onCategorySelect('outcall')}
              className={`flex-1 ${selectedCategory === 'outcall' ? 'bg-pink-500 hover:bg-pink-600 text-white' : 'hover:bg-pink-50'}`}
            >
              <MapPin size={16} className="mr-2" />
              {t('profile:rates_outcall', { defaultValue: 'Outcall' })}
            </Button>
          )}
          {/* Add button for 'special' if needed, or handle separately */} 
        </div>
      </div>

      {/* Duration/Rate Selection for the chosen category */}
      {selectedCategory && filteredRates.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-3">{t('profile:select_duration', { defaultValue: 'Choisir la durée' })}</h3>
          <RadioGroup 
            value={selectedRateId ?? undefined}
            onValueChange={onRateSelect} 
            className="space-y-2"
          >
            {filteredRates.map((rate) => {
              // Display duration/detail and price
              const displayDuration = formatDuration(rate.duration, t) || rate.description;
              
              return (
                <Label 
                  key={rate.id} 
                  htmlFor={rate.id}
                  className={`flex items-center justify-between p-3 border rounded-md cursor-pointer transition-colors ${
                    selectedRateId === rate.id ? 'border-pink-500 bg-pink-50' : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value={rate.id} id={rate.id} />
                    {/* Display Duration/Detail only */}
                    <span className="text-sm font-medium">{displayDuration}</span> 
                  </div>
                  {/* Price aligned to the right */}
                  <p className={`font-semibold text-sm whitespace-nowrap pl-2 ${selectedRateId === rate.id ? 'text-pink-600' : ''}`}> 
                    {rate.price} {rate.currency}
                  </p>
                </Label>
              );
            })}
          </RadioGroup>
        </div>
      )}
      
      {/* Optional: Message if a category is selected but has no rates (shouldn't happen with availableCategories check?) */}
      {selectedCategory && filteredRates.length === 0 && (
         <div className="text-sm text-muted-foreground italic p-3 bg-muted/50 rounded-md text-center">
            {t('profile:no_rates_for_category', { defaultValue: 'Aucun tarif défini pour cette catégorie.'})}
         </div>
      )}

    </div>
  );
}; 